/**
 * Input Factory Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 *
 * Centralizes input rendering logic with better type safety
 */
import React from "react";

import { FieldSchema } from "../types";
import { ArrayInput } from "./inputs/array-input";
import { BooleanInput } from "./inputs/boolean-input";
import { EnumInput } from "./inputs/enum-input";
import { MessageInput } from "./inputs/message-input";
import { NumberInput } from "./inputs/number-input";
import { RepeatedMessageInput } from "./inputs/repeated-message-input";
import { TextInput } from "./inputs/text-input";
import { TimestampInput, isTimestampField } from "./inputs/timestamp-input";

interface InputFactoryProps {
  field: FieldSchema;
  value: unknown;
  onChange: (fieldName: string, value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const InputFactory: React.FC<InputFactoryProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const handleChange = (newValue: unknown) => {
    onChange(field.name, newValue);
  };

  const commonProps = {
    field,
    value,
    onChange: handleChange,
    error,
    touched,
  };

  // Handle repeated fields (arrays)
  if (field.isRepeated) {
    if (field.messageFields && field.messageFields.length > 0) {
      return <RepeatedMessageInput {...commonProps} />;
    }
    return <ArrayInput {...commonProps} />;
  }

  // Handle different field types
  switch (field.type) {
    case "boolean":
      return <BooleanInput {...commonProps} />;

    case "enum":
      return <EnumInput {...commonProps} />;

    case "number":
    case "int32":
    case "uint32":
    case "float":
      return <NumberInput {...commonProps} />;

    case "message":
      if (isTimestampField(field)) {
        return <TimestampInput {...commonProps} />;
      }
      return <MessageInput {...commonProps} />;

    case "string":
    case "bytes":
    default:
      return <TextInput {...commonProps} />;
  }
};
